/**
 * useFingeringEditorMobile (issue #22 Phase B)
 *
 * Mobile (touch) 固有のジェスチャ・state・lifecycle を担当する hook。
 * spec: docs/superpowers/specs/2026-05-24-fingering-editor-3layer-split-design.md §3.2.2
 * plan: docs/superpowers/plans/2026-05-24-fingering-editor-3layer-split.md Phase B
 *
 * 元 useFingeringEditor.ts の touch / long-press / pinch / singleFingerPan 経路を抽出。
 * 元のロジックを忠実に再現することを最優先とする (元コード L260-265, 468-526, 528-548, 618-805, 946-951)。
 *
 * NOTE: 本ファイルは Phase B 時点では未使用 (誰からも呼ばれない)。Phase D で wrapper の compose 元になる。
 */
import { useCallback, useRef, useEffect, MutableRefObject } from 'react';
import { UseFingeringEditorCoreReturn } from './useFingeringEditorCore';
import { DragState, EditorMode } from '../types/fingeringEditor';
// TODO: REMOVE AFTER #5 DEBUG — issue #28 Phase 2-B 観測強化 (C-2 throttle)
import { dragDebug, dragDebugThrottled } from '../pages/fingering/components/editor/dragDebug';

const LONG_PRESS_MS = 200;
const DRAG_THRESHOLD_PX = 5;
const DRAG_THRESHOLD_SQ = DRAG_THRESHOLD_PX * DRAG_THRESHOLD_PX;

function touchDist(a: Touch, b: Touch): number {
  const dx = a.clientX - b.clientX;
  const dy = a.clientY - b.clientY;
  return Math.sqrt(dx * dx + dy * dy);
}

// issue #16 v3 Phase C: Mobile (touch) の空白起点 範囲選択 drag の internal state。
// Desktop の SelectDragState (useFingeringEditorDesktop.ts) の touch 版。
// move モード + 1 本指 + 空白起点 (dragRef==null) でのみ起動する。
interface SelectTouchState {
  startSvgX: number;
  startSvgY: number;
  startClientX: number;
  startClientY: number;
  page: number;
  // 'pending' → 移動閾値超過で 'dragging' に昇格
  phase: 'pending' | 'dragging';
}

export interface UseFingeringEditorMobileReturn {
  refs: {
    pinchRef: MutableRefObject<{ dist: number; zoom: number }>;
    panStartRef: MutableRefObject<{ x: number; y: number } | null>;
    singleFingerPanStartRef: MutableRefObject<{ x: number; y: number } | null>;
    singleFingerPanOriginRef: MutableRefObject<{ x: number; y: number }>;
    longPressRef: MutableRefObject<ReturnType<typeof setTimeout> | null>;
    longPressTriggeredRef: MutableRefObject<boolean>;
  };
  handlers: {
    handleTouchStart: (e: React.TouchEvent) => void;
    handleTouchEnd: () => void;
    handleBadgeDragStart: (id: number, e: React.PointerEvent) => void;
    handlePointerMove: (e: React.PointerEvent<SVGSVGElement>) => void;
    handlePointerUp: () => void;
    handlePointerLeave: (e?: React.PointerEvent<SVGSVGElement>) => void;
    handleEditorModeChange: (mode: EditorMode) => void;
  };
}

export function useFingeringEditorMobile(core: UseFingeringEditorCoreReturn): UseFingeringEditorMobileReturn {
  // --- Mobile 専用 ref ---
  const pinchRef = useRef({ dist: 0, zoom: 1 });
  const panStartRef = useRef<{ x: number; y: number } | null>(null);
  // issue #16 Phase H-1: edit + 1 本指空白 = PDFパン (案 A、 zoom 不問)。
  //   handleTouchStart で start (touch 座標) + origin (panRef スナップショット) を確定、
  //   onTouchMove で next = origin + (current - start) → applyPanClamp → setPan。
  //   clampPan が container/wrapper の rect で「収まる軸→0 / はみ出る軸→有効値」に正規化するため
  //   zoom 値ガードは不要 (画面サイズで自動分岐)。
  //   handleTouchEnd は setPan(applyPanClamp(panRef)) で再クランプ (pinch→zoom=1 戻し時の pan 宙吊り防止)。
  const singleFingerPanStartRef = useRef<{ x: number; y: number } | null>(null);
  const singleFingerPanOriginRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const longPressRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressTriggeredRef = useRef(false);
  // issue #16 v3 Phase C: 空白起点 範囲選択 drag (move モード + 1 本指)。 Mobile hook 内部のみで完結。
  const selectTouchRef = useRef<SelectTouchState | null>(null);

  // --- e5 onTouchMove: passive:false で touchmove listener を登録 (pinch / singleFingerPan / move preventDefault) ---
  // 元 useFingeringEditor.ts L468-526 のうち touchmove 部分のみ Mobile へ。
  useEffect(() => {
    if (!core.view.isMobile) return;
    const el = core.refs.scrollContainerRef.current;
    if (!el) return;

    const onTouchMove = (e: TouchEvent) => {
      // 案 P-1: move モードでは縦スクロール判定より先に preventDefault で pointercancel 防止
      if (core.refs.editorModeRef.current === 'move') e.preventDefault();
      // zoom>1 時はブラウザネイティブのスクロール/バウンスを無効化
      if (core.refs.zoomRef.current > 1) e.preventDefault();
      // --- 2 本指: pinch zoom + pan (spec §3.3、 edit/move 両方で有効) ---
      if (e.touches.length === 2) {
        e.preventDefault();
        const dist = touchDist(e.touches[0], e.touches[1]);
        const rawScale = dist / pinchRef.current.dist;
        const scale = 1 + (rawScale - 1) * 3.2;
        const nextZoom = Math.min(5, Math.max(1, pinchRef.current.zoom * scale));
        const cx = (e.touches[0].clientX + e.touches[1].clientX) / 2;
        const cy = (e.touches[0].clientY + e.touches[1].clientY) / 2;
        core.handlers.applyZoomWithPivot(cx, cy, nextZoom);
        // 2 本指 pan: 中心の移動に追従 (panStartRef は touchstart で中心-pan の差分を保持)。
        // 旧来は panStartRef に値を入れるだけで pan 適用が無かった (1本指PDFパンに依存)。
        // Phase C で 1 本指 PDF パンを撤廃したため、 pan を 2 本指へ一本化する。
        if (panStartRef.current) {
          const next = { x: cx - panStartRef.current.x, y: cy - panStartRef.current.y };
          core.view.setPan(core.handlers.applyPanClamp(next));
        }
        return;
      }
      // --- 1 本指 ---
      if (e.touches.length === 1) {
        // issue #16 Phase H-1: edit + 1 本指 = PDFパン (案 A、 zoom 不問、 clampPan で画面判定)。
        //   singleFingerPanStartRef は handleTouchStart で set 済み (空白起点のみ)。
        //   applyPanClamp が「収まる軸→0 / はみ出る軸→有効値」に正規化するため zoom 値ガードなし。
        if (core.refs.editorModeRef.current !== 'move') {
          const start = singleFingerPanStartRef.current;
          if (!start) return;
          const t = e.touches[0];
          const origin = singleFingerPanOriginRef.current;
          const next = {
            x: origin.x + (t.clientX - start.x),
            y: origin.y + (t.clientY - start.y),
          };
          core.view.setPan(core.handlers.applyPanClamp(next));
          return;
        }
        // badge 起点 (dragRef!=null) は個別移動経路 (handlePointerMove) に委ねる → 範囲選択しない。
        if (core.refs.dragRef.current) return;
        // 空白起点の範囲選択 drag (move モード)。 selectTouchRef は touchstart で初期化済。
        const sel = selectTouchRef.current;
        if (!sel) return;
        e.preventDefault();
        const t = e.touches[0];
        const dx = t.clientX - sel.startClientX;
        const dy = t.clientY - sel.startClientY;
        if (sel.phase === 'pending') {
          if (dx * dx + dy * dy < DRAG_THRESHOLD_SQ) return;
          sel.phase = 'dragging';
        }
        // svg 座標が取れないとき (jsdom 等で rect=0) は client delta を流用して矩形 visible のみ更新する
        // (Desktop handlePointerMove L202-205 と同方針)。
        const svgPt = core.handlers.clientToSvg(t.clientX, t.clientY);
        const currentSvgX = svgPt?.x ?? sel.startSvgX + dx;
        const currentSvgY = svgPt?.y ?? sel.startSvgY + dy;
        core.mode.setSelectRectVisible({
          startSvgX: sel.startSvgX,
          startSvgY: sel.startSvgY,
          currentSvgX,
          currentSvgY,
          page: sel.page,
        });
      }
    };

    el.addEventListener('touchmove', onTouchMove, { passive: false });
    return () => {
      el.removeEventListener('touchmove', onTouchMove);
    };
    // 元 e5 と同じく fingeringData 依存 (loading 完了後にエディタ DOM が存在する状態でリスナー登録)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [core.view.isMobile, core.data.fingeringData]);

  // --- e4: unmount 時 long-press timeout cleanup ---
  useEffect(() => () => {
    if (longPressRef.current) clearTimeout(longPressRef.current);
  }, []);

  // --- handleTouchStart (元 L528-542 + issue #16 v3 Phase C 再編) ---
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      // 2 本指開始: pinch zoom の基準 + pan の中心-pan 差分 (panStartRef) を確定。
      pinchRef.current = {
        dist: touchDist(e.touches[0] as unknown as Touch, e.touches[1] as unknown as Touch),
        zoom: core.refs.zoomRef.current,
      };
      const cx = (e.touches[0].clientX + e.touches[1].clientX) / 2;
      const cy = (e.touches[0].clientY + e.touches[1].clientY) / 2;
      panStartRef.current = { x: cx - core.refs.panRef.current.x, y: cy - core.refs.panRef.current.y };
      // 2 本指に移行したら 1 本指範囲選択は中断する (誤起動防止)。
      selectTouchRef.current = null;
    }
    // 1 本指 + move + 空白起点 (dragRef==null): 範囲選択 drag を初期化 (spec §3.3)。
    // badge 起点は handleBadgeDragStart が dragRef を立てるので、 ここでは空白のみ拾う。
    if (
      e.touches.length === 1 &&
      core.refs.editorModeRef.current === 'move' &&
      !core.refs.dragRef.current
    ) {
      const t = e.touches[0];
      const svgPt = core.handlers.clientToSvg(t.clientX, t.clientY);
      selectTouchRef.current = {
        startSvgX: svgPt?.x ?? 0,
        startSvgY: svgPt?.y ?? 0,
        startClientX: t.clientX,
        startClientY: t.clientY,
        page: core.view.currentPage,
        phase: 'pending',
      };
    }
    // issue #16 Phase H-1: edit + 1 本指空白 = PDFパン 起動 (案 A、 zoom 不問)。
    //   badge 起点 (dragRef!=null) は handleBadgeDragStart が dragRef を立てるのでスキップ → 空白のみ拾う。
    //   start (touch 座標) + origin (panRef スナップショット) を set し、 onTouchMove で差分を applyPanClamp 経由で setPan。
    if (
      e.touches.length === 1 &&
      core.refs.editorModeRef.current !== 'move' &&
      !core.refs.dragRef.current
    ) {
      const t = e.touches[0];
      singleFingerPanStartRef.current = { x: t.clientX, y: t.clientY };
      singleFingerPanOriginRef.current = { ...core.refs.panRef.current };
    }
  }, [core.refs.editorModeRef, core.refs.panRef, core.refs.zoomRef, core.refs.dragRef, core.handlers, core.view]);

  // --- handleTouchEnd (元 L544-548 + issue #16 v3 Phase C 範囲選択 finalize) ---
  const handleTouchEnd = useCallback(() => {
    // issue #16 v3 Phase E+F (DRY): 空白起点 範囲選択 drag の finalize は Core の共通ヘルパーへ集約。
    //   (旧 Phase C では Desktop handlePointerUp のコピーだった min/max→badgesInRect→selectRange を共通化)
    const sel = selectTouchRef.current;
    if (sel) {
      if (sel.phase === 'dragging') {
        core.handlers.commitRangeSelectFromVisible();
      } else {
        // tap (閾値未満) は何もせず矩形オーバーレイだけ片付ける。
        core.mode.setSelectRectVisible(null);
      }
      selectTouchRef.current = null;
    }
    panStartRef.current = null;
    singleFingerPanStartRef.current = null;
    // issue #16 Phase H-1 R-6: 旧 `zoom<=1 で setPan{0,0}` を「panRef を再クランプ」へ置換。
    //   clampPan が「収まる軸→0 / はみ出る軸→保持」に正規化するため、
    //   pinch で zoom=1 に戻したときの pan 宙吊り (旧リセットの役割) を構造的に代替しつつ、
    //   等倍ではみ出しがあれば pan を消さない (案 A、 ユーザー要望と一致)。
    core.view.setPan(core.handlers.applyPanClamp(core.refs.panRef.current));
  }, [core.refs.panRef, core.view, core.mode, core.handlers]);

  // --- handleBadgeDragStart (元 L618-645) touch 経路 ---
  // 元コードは pointerType を区別せず常に long-press タイマー起動 + dragRef pending。
  // wrapper 側で pointerType ガード (mouse 以外) を行っているため、 Mobile hook では追加ガード不要。
  // issue #16 Phase 6: 選択モード中は drag を起動せず toggle(id) のみ実行 (M-A)。
  const handleBadgeDragStart = useCallback((id: number, e: React.PointerEvent) => {
    // TODO: REMOVE AFTER #27 DEBUG — issue #5 Phase R 真因切り分け
    dragDebug(`down badge=${id} mode=${core.refs.editorModeRef.current} type=${e.pointerType}`);
    // issue #16 v3 Phase B: 起点分岐モデル。 badge 起点は常に drag 経路を起動する
    //   (move なら個別移動 / edit なら handlePointerMove L176 で昇格拒否 = 移動不可)。
    //   tap (移動閾値未満) のときは handlePointerUp が選択する (move=選択 / edit=選択+panel)。
    //   旧 Phase 6 の「selectMode 中は即 toggle して drag をスキップ」は撤廃した
    //   (move で個別移動できなくなり spec §3.3 と矛盾するため)。
    // Core の tryStartDrag が clickSuppressRef / clientToSvg / dragRef / setPointerCapture を担う
    const started = core.handlers.tryStartDrag(id, e);
    if (!started) return;
    longPressTriggeredRef.current = false;
    if (longPressRef.current) clearTimeout(longPressRef.current);
    longPressRef.current = setTimeout(() => {
      longPressTriggeredRef.current = true;
    }, LONG_PRESS_MS);
  }, [core.handlers, core.refs.editorModeRef]);

  // --- handlePointerMove (元 L647-709) touch / 空文字 経路 ---
  // 元コードの drag 進行 + long-press miss 判定をすべて忠実に再現。
  // panDrag は Desktop 専用なのでここでは扱わない。
  // wrapper 側で「pointerType === 'mouse' は Desktop」と振り分け済 → ここに来るのは touch / pen / 空文字 (jsdom)。
  const handlePointerMove = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
    const d = core.refs.dragRef.current;
    if (!d) return;
    if (d.phase === 'pending') {
      const dx = e.clientX - d.startClientX;
      const dy = e.clientY - d.startClientY;
      const distSq = dx * dx + dy * dy;
      if (core.mode.addMode) {
        if (distSq > DRAG_THRESHOLD_SQ) {
          dragDebug('phase=null (addMode tap-cancel)');
          core.refs.dragRef.current = null;
          if (longPressRef.current) clearTimeout(longPressRef.current);
          core.refs.clickSuppressRef.current = true;
        }
        return;
      }
      // issue #45 バグ A (1タップ反応): 閾値未満の微振動は edit reject より前に早期 return。
      //   旧: 微振動でも先に edit reject 経由で dragRef=null → handlePointerUp 到達せず handleBadgeSelect 未呼び。
      //   新: 微振動は pending 保持 → handlePointerUp tap 経路 (edit→handleBadgeSelect、 move→toggle) で正しく選択。
      if (distSq < DRAG_THRESHOLD_SQ) return;
      if (core.mode.editorMode !== 'move') {
        dragDebug('phase=null (touch + edit reject)');
        core.refs.dragRef.current = null;
        // 閾値超過後の svg click を吸収 (panel 誤閉防止、 issue #45 バグ A 修正)。
        core.refs.clickSuppressRef.current = true;
        return;
      }
      dragDebug(`phase=dragging mode=${core.refs.editorModeRef.current} type=${e.pointerType}`);
      d.phase = 'dragging';
      core.drag.setDragBadgeId(d.badgeId);
      try { core.refs.svgRef.current?.setPointerCapture?.(e.pointerId); } catch { /* jsdom 等を許容 */ }
      return;
    }
    const svgPt = core.handlers.clientToSvg(e.clientX, e.clientY);
    if (!svgPt) return;
    d.currentX = svgPt.x;
    d.currentY = svgPt.y;
    // TODO: REMOVE AFTER #5 DEBUG — C-2 (issue #28 Phase 2-B): pointermove dragDelta 観測 (5 回ごとに throttle)
    dragDebugThrottled(
      'move',
      5,
      `move clientY=${Math.round(e.clientY)} svgY=${Math.round(svgPt.y)} dy=${Math.round(svgPt.y - d.startSvgY)}`,
    );
    core.drag.setDragDelta({ dx: svgPt.x - d.startSvgX, dy: svgPt.y - d.startSvgY });
  }, [core.handlers, core.mode.addMode, core.mode.editorMode, core.refs, core.drag]);

  // --- handlePointerUp (元 L758-785) touch 経路 ---
  const handlePointerUp = useCallback(() => {
    // TODO: REMOVE AFTER #27 DEBUG — pointerup が pointercancel と排他か検証
    dragDebug(`up phase=${core.refs.dragRef.current?.phase ?? 'null'}`);
    if (longPressRef.current) clearTimeout(longPressRef.current);
    const d = core.refs.dragRef.current;
    if (!d) return;
    if (d.phase === 'pending') {
      core.refs.dragRef.current = null;
      core.refs.clickSuppressRef.current = true;
      if (core.mode.addMode) {
        if (d.beatIndex !== null) core.handlers.addNoteToBeat(d.beatIndex);
        return;
      }
      // issue #164: badge 起点 tap (移動閾値未満) の分岐。
      //   move かつ範囲選択の最中 (selectedCount>0) → 従来通り toggle (選択の追加/除外に専念)。
      //   それ以外 (move で未選択 / edit) → handleBadgeSelect (単音=panel / 和音=chordPicker を開く)。
      //   旧 Phase B は move では常に toggle だったため、 単音/和音 tap で編集 UI が出せなかった。
      if (core.mode.editorMode === 'move' && core.mode.selectedCount > 0) {
        core.handlers.toggle(d.badgeId);
      } else {
        core.handlers.handleBadgeSelect(d.badgeId, d.startClientX, d.startClientY);
      }
      return;
    }
    // P-2 (issue #28 v3): Mobile (touch) 経路では applyTouchOffset=isMobile で視覚位置 drop を有効化
    core.handlers.finalizeDrag(d as DragState, { applyTouchOffset: core.view.isMobile });
  }, [core.handlers, core.mode.addMode, core.mode.editorMode, core.mode.selectedCount, core.refs, core.view.isMobile]);

  // --- handlePointerLeave (元 L787-804) touch 経路 ---
  const handlePointerLeave = useCallback((e?: React.PointerEvent<SVGSVGElement>) => {
    if (longPressRef.current) clearTimeout(longPressRef.current);
    const d = core.refs.dragRef.current;
    if (!d) return;
    // V3-3: capture を保持中の pointerleave は無視 (iOS Safari の誤発火対策)
    if (e && core.refs.svgRef.current?.hasPointerCapture?.(e.pointerId)) return;
    if (d.phase === 'dragging') {
      // P-2 (issue #28 v3): pointerLeave 経由でも視覚位置 drop を適用
      core.handlers.finalizeDrag(d as DragState, { applyTouchOffset: core.view.isMobile });
    } else {
      core.refs.dragRef.current = null;
    }
  }, [core.handlers, core.refs, core.view.isMobile]);

  // --- handleEditorModeChange (元 L946-951 + issue #16 v3 Phase A: selectMode 派生化) ---
  const handleEditorModeChange = useCallback((mode: EditorMode) => {
    core.handlers.discardEmptyAddedBadge();
    core.mode.setEditorMode(mode);
    if (mode === 'move') {
      core.mode.setAddMode(false);
      // selectMode は派生値 (Core hook の useMemo: !isMobile || editorMode==='move') なので
      // ここで明示的に切り替えない (editorMode='move' で自動的に true になる)。
    }
    core.handlers.showToast(mode === 'edit' ? '編集モードに切り替えました' : '運指移動モードに切り替えました');
  }, [core.mode, core.handlers]);

  return {
    refs: {
      pinchRef, panStartRef, singleFingerPanStartRef, singleFingerPanOriginRef,
      longPressRef, longPressTriggeredRef,
    },
    handlers: {
      handleTouchStart, handleTouchEnd,
      handleBadgeDragStart, handlePointerMove, handlePointerUp, handlePointerLeave,
      handleEditorModeChange,
    },
  };
}
